import {ListTableDataSourceFragment} from "@/shared/ListTableDataSourceFragment";
import {
  LABELER_IMPORT_STATUS_KTM,
  LabelerImport,
  LabelerImports,
  LabelerTask,
  ResourceData
} from "@/fyneai-shared/types";
import {BaseSelectTableDataRow, TableDataContainer, TableDataRow} from "@/shared/TableDataContainer";
import {TableDataSourceFragmentProps} from "@/shared/TableDataSourceFragment";
import React from "react";
import {TableData} from "@/shared/tabledata";
import {PageFragment, PageFragmentStyleFlags} from "../../../../shared/PageFragment";
import {Action, ACTION_SPACE, ActionBase, CreateActionsArgs, FilterListItemsLoader} from "../../../../shared/types";
import {AddOutlined} from "@mui/icons-material";
import {EditImportHelper} from "./EditImportHelper";
import {DateUtil, FORMAT_SHOW_TIME} from "../../../../shared/date_util";
import {Api} from "../../../../fyneai-shared/api";
import {BaseApp} from "@/shared/BaseApp";

class LabelerImportRow extends BaseSelectTableDataRow {

  constructor(readonly projectId: string, readonly datasetId: string) {
    super();
  }

  @TableData({name: "Name"})
  name: string;

  @TableData({name: "Created", cellSize: "large"})
  created: string;

  @TableData({name: "File", cellSize: "xxlarge"})
  fileUrl: string;

  @TableData({
    name: "Status",
    type: "enum",
    enumValues: LABELER_IMPORT_STATUS_KTM.kts().values,
    enumColors: LABELER_IMPORT_STATUS_KTM.metadataValues("color").values,
    cellSize: "large"
  })
  status: string;

  @TableData({name: "Assets", cellSize: "medium"})
  assets: string;

  @TableData({
    name: "Actions",
    type: "actions",
    actions: (event, args: CreateActionsArgs<LabelerImport>) => {
      const row: LabelerImportRow = args.rowValue.__row;
      return [
        new Action("Process", () => Api.getInstance().labelerImportProcess(row.projectId, row.datasetId, args.data.id)),
        new Action("Delete", () => {
          const loader = new LabelerImports(row.projectId, row.datasetId);
          loader.deleteListItem(args.data)
            .then(() => BaseApp.CONTEXT.showToast("Import deleted."));
        }),
      ]
    }
  })
  actions: any;
}

export type ImportsFragmentProps = TableDataSourceFragmentProps & {
  datasetId?: string,
}

export class ImportsFragment extends ListTableDataSourceFragment<LabelerImport, LabelerImportRow, LabelerImports, ImportsFragmentProps> {

  private readonly actions: ActionBase[] = [
    ACTION_SPACE,
    !this.props.tableSelectModeConfig
      ? new Action("Add import", () => {
        EditImportHelper.addImport(this.props.datasetId);
      }, AddOutlined).setVariant("contained")
      : null,
  ].filter(Boolean);

  constructor(props: ImportsFragmentProps, context: any) {
    super(props, context, new LabelerImports(ResourceData.projectId(), props.datasetId));
  }

  protected styleFlags(): number | PageFragmentStyleFlags {
    return PageFragment.STYLE_TOOLBAR_TYPE_FLAG;
  }

  protected getTitle(): string | null | undefined {
    return "Imports";
  }

  protected getToolbar(toolbarModeId: string | null): React.ReactElement {
    return this.renderToolbarButtonsInToolbarContainerEx(this.actions);
  }

  applyTableDataToRow(data: LabelerImport, row: LabelerImportRow): void {
    row.name = data.name;
    row.created = DateUtil.formatDateTime(data.createdAt, FORMAT_SHOW_TIME);
    row.fileUrl = data.fileUrl;
    row.status = data.status;
    row.assets = (data.assets?.length || 0).toString();
  }

  createTableDataRow(): LabelerImportRow {
    return new LabelerImportRow(ResourceData.projectId(), this.props.datasetId);
  }

  onTableCellChanged(data: LabelerImport, cellId: string, value: any): void {
  }

  onTableDataSelected(data: LabelerImport): void {
    EditImportHelper.editImport(this.props.datasetId, data);
  }

  renderContent(): React.ReactElement {
    return <TableDataContainer
      path={this.props.path}
      data={this.loader.getListItems()}
      source={this}
      selectModeConfig={this.props.tableSelectModeConfig}
      multiSelectListener={this.props.tableMultiSelectListener}
    />;
  }
}

class LabelerTaskImportRow implements TableDataRow {

  @TableData({name: "Name"})
  name: string;

  @TableData({name: "Created", cellSize: "large"})
  created: string;

  @TableData({name: "File", cellSize: "xxlarge"})
  fileUrl: string;

  @TableData({
    name: "Status",
    type: "enum",
    enumValues: LABELER_IMPORT_STATUS_KTM.kts().values,
    enumColors: LABELER_IMPORT_STATUS_KTM.metadataValues("color").values,
    cellSize: "large"
  })
  status: string;

  @TableData({name: "Assets", cellSize: "medium"})
  assets: string;
}

export type TaskImportsFragmentProps = ImportsFragmentProps & {
  task: LabelerTask,
}

class LabelerTaskImports extends FilterListItemsLoader<LabelerImport, LabelerImports> {
}

export class TaskImportsFragment extends ListTableDataSourceFragment<LabelerImport, LabelerTaskImportRow, LabelerTaskImports, TaskImportsFragmentProps> {

  constructor(props: TaskImportsFragmentProps, context: any) {
    super(props, context, new LabelerTaskImports(new LabelerImports(ResourceData.projectId(), props.datasetId), (import_: LabelerImport) => {
      console.log("id: " + import_.id);
      return props.task.importIds.includes(import_.id);
    }));
  }

  protected styleFlags(): number | PageFragmentStyleFlags {
    return PageFragment.STYLE_TOOLBAR_TYPE_FLAG;
  }

  protected getTitle(): string | null | undefined {
    return "Imports";
  }

  applyTableDataToRow(data: LabelerImport, row: LabelerImportRow): void {
    row.name = data.name;
    row.created = DateUtil.formatDateTime(data.createdAt, FORMAT_SHOW_TIME);
    row.fileUrl = data.fileUrl;
    row.status = data.status;
    row.assets = (data.assets?.length || 0).toString();
  }

  createTableDataRow(): LabelerTaskImportRow {
    return new LabelerTaskImportRow();
  }

  onTableCellChanged(data: LabelerImport, cellId: string, value: any): void {
  }

  onTableDataSelected(data: LabelerImport): void {
    EditImportHelper.editImport(this.props.datasetId, data);
  }

  renderContent(): React.ReactElement {
    return <TableDataContainer
      disableSelection={true}
      path={this.props.path}
      data={this.loader.getListItems()}
      source={this}
    />;
  }
}
